import React, { useState, useEffect } from 'react';
import { CodeBracketIcon, BeakerIcon, ChartBarIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import enhancedRequest from '../../api/enhancedApiService';

/**
 * DBT Metadata Card Component
 *
 * Displays DBT metadata for a table if it's linked to a DBT model
 */
const DbtMetadataCard = ({ connectionId, tableName, database, schema }) => {
  const [dbtMetadata, setDbtMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDbtMetadata = async () => {
      if (!connectionId || !tableName || !database || !schema) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Call the API endpoint to get DBT metadata
        const params = new URLSearchParams({
          connection_id: connectionId,
          database: database,
          schema: schema,
          table: tableName
        });

        const response = await enhancedRequest(`/tables/${tableName}/dbt?${params.toString()}`);

        if (response.has_dbt_metadata && response.dbt_metadata) {
          setDbtMetadata(response.dbt_metadata);
        } else {
          setDbtMetadata(null);
        }
      } catch (err) {
        console.error('Error fetching DBT metadata:', err);
        setError(err.message);
        setDbtMetadata(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDbtMetadata();
  }, [connectionId, tableName, database, schema]);

  // Don't render anything while loading or if there's no metadata
  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center">
            <LoadingSpinner size="sm" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !dbtMetadata) {
    return null; // Don't show anything if there's no DBT metadata
  }

  const { model, project, tests, test_count, latest_run } = dbtMetadata;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden shadow-sm rounded-lg border-2 border-blue-200">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
              <CodeBracketIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">dbt Model</h3>
              <p className="text-sm text-gray-600">{project.name}</p>
            </div>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            dbt
          </span>
        </div>

        <div className="space-y-3">
          {/* Model Name */}
          <div className="flex items-center text-sm">
            <span className="text-gray-500 w-32">Model:</span>
            <span className="font-medium text-gray-900">{model.name}</span>
          </div>

          {/* Model Type */}
          {model.model_type && (
            <div className="flex items-center text-sm">
              <span className="text-gray-500 w-32">Type:</span>
              <span className="font-medium text-gray-700">{model.model_type}</span>
            </div>
          )}

          {/* Materialization */}
          {model.materialization && (
            <div className="flex items-center text-sm">
              <span className="text-gray-500 w-32">Materialization:</span>
              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{model.materialization}</span>
            </div>
          )}

          {/* Description */}
          {model.description && (
            <div className="text-sm mt-3">
              <span className="text-gray-500 block mb-1">Description:</span>
              <p className="text-gray-700 text-xs">{model.description}</p>
            </div>
          )}

          {/* Tags */}
          {model.tags && model.tags.length > 0 && (
            <div className="flex items-start text-sm">
              <span className="text-gray-500 w-32">Tags:</span>
              <div className="flex flex-wrap gap-1">
                {model.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tests Count */}
          <div className="flex items-center text-sm pt-2 border-t border-blue-200">
            <BeakerIcon className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-gray-700">
              {test_count} dbt {test_count === 1 ? 'test' : 'tests'}
            </span>
          </div>

          {/* Latest Run */}
          {latest_run && (
            <div className="flex items-center text-sm">
              <ClockIcon className="h-4 w-4 text-gray-500 mr-2" />
              <span className="text-gray-700">
                Last run: {new Date(latest_run.completed_at).toLocaleString()}
              </span>
              {latest_run.status === 'success' && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Success
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DbtMetadataCard;
